/**
 * F4.10 phone validation: Close delivery stays disabled until every check is green, and the
 * screen names the first failing one. Pure; unit-tested against the spec's acceptance criteria.
 */
import {
  bagWeight,
  counts,
  hasMainLot,
  isV1,
  lotPlan,
  slotOf,
  v1BagsDone,
  v1MoistureDone,
  type DeliveryDraft,
} from './delivery'

export type CloseCheckKey =
  | 'storage' // both home-storage answers
  | 'bags_counted'
  | 'failed_bags' // each failed bag has a reason and a photo
  | 'probe_draws'
  | 'accepted' // price accepted (and a usable card per lot)
  | 'tags' // tags = bags for each lot
  | 'weights' // sack type and weight on every bag
  | 'moisture' // groundnut: readings or the supervisor's skip
  | 'sample' // MAIN lot: sealed, weighed to the minimum (or allowed), photographed
  | 'disputes' // every dispute has an outcome
  | 'agreed' // Farmer agrees, or the delivery is marked disputed
  | 'slip_no'
  | 'slip_photo'
  | 'bags_tagged' // V1: at least one bag, each tagged and weighed
  | 'sealed' // V1: MAIN lot: the sample sealed (no weight, no photo)
  | 'cards' // V1: a usable price for every lot (no price screen)
  | 'location' // new live drafts: the phone's position, or a supervisor's leave

export interface CloseCheck {
  key: CloseCheckKey
  ok: boolean
  /** Values for the message, e.g. { lot: 'HL-…', tagged: 8, bags: 9 }. */
  vars?: Record<string, string | number>
}

export interface CloseContext {
  /** Crop config: moisture reading required (groundnut). */
  moistureRequired: boolean
  sampleMinG: number
  /** Usable rate card per stream present in the delivery. */
  cardsOk: boolean
}

export function closeChecks(d: DeliveryDraft, ctx: CloseContext): CloseCheck[] {
  if (isV1(d)) return closeChecksV1(d, ctx)
  const { brought, failed } = counts(d)
  const plan = lotPlan(d)
  const main = hasMainLot(d)
  const checks: CloseCheck[] = []

  checks.push({
    key: 'storage',
    ok: d.storage.stored_over_7_days !== null && d.storage.tablet_or_powder !== null,
  })
  checks.push({ key: 'bags_counted', ok: brought >= 1 })
  checks.push({
    key: 'failed_bags',
    ok: failed <= brought && d.failed_bags.every((f) => f.reason_code && f.photo_media_id),
  })
  checks.push({ key: 'probe_draws', ok: !main || d.probe_draws_done })
  checks.push({ key: 'accepted', ok: d.outcome !== null && ctx.cardsOk })

  for (const lot of plan) {
    const tagged = d.bags.filter((b) => slotOf(d, b) === lot.slot && !b.withdrawn).length
    checks.push({
      key: 'tags',
      ok: tagged === lot.bag_count,
      vars: { lot: lot.code, tagged, bags: lot.bag_count },
    })
  }
  checks.push({
    key: 'weights',
    ok: d.bags.every(
      (b) =>
        b.sack_type_id &&
        bagWeight(b) !== null &&
        bagWeight(b)!.gross_g - b.tare_g > 0 &&
        (!d.reading_photos_required || b.weights.every((w) => !!w.photo_media_id)),
    ),
  })

  if (main) {
    checks.push({
      key: 'moisture',
      ok: ctx.moistureRequired
        ? (d.moisture.readings.length >= 2 &&
            (!d.reading_photos_required || d.moisture.readings.every((r) => !!r.photo_media_id))) ||
          d.moisture.skip_allowed_by !== null
        : d.moisture.saved &&
          (!d.reading_photos_required || d.moisture.readings.every((r) => !!r.photo_media_id)),
    })
    const s = d.sample
    checks.push({
      key: 'sample',
      ok:
        s !== null &&
        !!s.photo_media_id &&
        ((s.weight_g ?? 0) >= ctx.sampleMinG || s.underweight_allowed_by !== null),
      vars: { min: ctx.sampleMinG },
    })
  }

  checks.push({ key: 'disputes', ok: d.disputes.every((x) => x.outcome !== null) })
  const paper = d.capture_mode === 'PAPER_BACKFILL'
  checks.push({ key: 'agreed', ok: paper || d.slip.farmer_agreed_at !== null || d.slip.disputed })
  checks.push({ key: 'slip_no', ok: !!d.slip.slip_no && /^\d+$/.test(d.slip.slip_no) })
  checks.push({
    key: 'slip_photo',
    ok: !!(paper ? (d.paper?.page_photo_media_id ?? d.slip.photo_media_id) : d.slip.photo_media_id),
  })
  return checks
}

/**
 * V1 (F4.10 'Final check'): tags, sample seal, the two questions, slip number, slip photo and
 * Farmer agrees — plus a moisture reading or 'No meter today' for groundnut. No bag count, no
 * price acceptance, no sample weight or photo.
 */
function closeChecksV1(d: DeliveryDraft, ctx: CloseContext): CloseCheck[] {
  const paper = d.capture_mode === 'PAPER_BACKFILL'
  const checks: CloseCheck[] = [
    {
      key: 'bags_tagged',
      ok: v1BagsDone(d),
      vars: { count: d.bags.length },
    },
  ]
  if (hasMainLot(d)) checks.push({ key: 'sealed', ok: d.sample !== null })
  checks.push({
    key: 'storage',
    ok: d.storage.stored_over_7_days !== null && d.storage.tablet_or_powder !== null,
  })
  if (d.moisture_required)
    checks.push({
      key: 'moisture',
      ok: v1MoistureDone(d),
    })
  checks.push({ key: 'cards', ok: ctx.cardsOk })
  checks.push({ key: 'slip_no', ok: !!d.slip.slip_no && /^\d+$/.test(d.slip.slip_no) })
  checks.push({
    key: 'slip_photo',
    ok: !!(paper ? (d.paper?.page_photo_media_id ?? d.slip.photo_media_id) : d.slip.photo_media_id),
  })
  checks.push({ key: 'agreed', ok: paper || d.slip.farmer_agreed_at !== null })
  if (d.location_required) checks.push(locationCheck(d))
  return checks
}

export function firstFailing(checks: readonly CloseCheck[]): CloseCheck | null {
  return checks.find((c) => !c.ok) ?? null
}

/** Last, so the position is taken at the end, where the purchase is closed. */
function locationCheck(d: DeliveryDraft): CloseCheck {
  return { key: 'location', ok: !!d.purchase_gps || !!d.location_skipped_by }
}
